import { FC, useMemo } from 'react';

import {
  DELIVERY_CHANNEL_LIST,
  DeliveryChannel,
  GiftCouponForRedemption,
  GiftCouponOrderInfo,
  GiftCouponRedeemStatus,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useGiftCsMemosByOrders } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import { GiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';

import { CouponHistorySearchHook } from '../../hooks/useCouponHistorySearch';
import { formatDateTimeSec } from '../../utils/couponSearch.utils';
import { CouponTableRow, VerificationData } from './CouponTableRow';

interface SearchResultsTableProps {
  hook: CouponHistorySearchHook;
  onOpenSignatureModal: (signatureImage: Image) => void;
  onOpenVerificationModal: (verification: VerificationData) => void;
  onOpenKakaoShareCallbacks: (coupon: GiftCouponForRedemption) => void;
  onOpenOrderModalByPhone: (phoneNumber: string) => void;
}

export const SearchResultsTable: FC<SearchResultsTableProps> = ({
  hook,
  onOpenSignatureModal,
  onOpenVerificationModal,
  onOpenKakaoShareCallbacks,
  onOpenOrderModalByPhone,
}) => {
  const {
    status,
    results,
    sortDirection,
    handleSort: onSort,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    selectedShareableCount,
    isUnsharing,
    handleUnshare: onUnshare,
    handleResetShareStatusOne: onResetShareStatus,
    channelEditMode,
    toggleChannelEditMode: onToggleChannelEditMode,
    channelSelectedIds,
    setChannelSelectedIds,
    bulkChannelValue,
    setBulkChannelValue,
    isSavingChannel,
    deliveredGroupMap,
    toggleChannelOne,
    toggleChannelGroup,
    handleBulkChannelApply: onBulkChannelApply,
    editingCouponId,
    editPhone,
    setEditPhone: onSetEditPhone,
    editMemo,
    setEditMemo: onSetEditMemo,
    startEditPhoneMemo: onStartEdit,
    saveEditPhoneMemo: onSaveEdit,
    cancelEditPhoneMemo: onCancelEdit,
    deletingId,
    handleDeleteAdditional: onDeleteAdditional,
    copyToClipboard: onCopyToClipboard,
    isSavingEdit,
    isIssuing,
    handleIssueRefund: onIssueRefund,
    handleIssueRenewal: onIssueRenewal,
    handleCancelIssue: onCancelIssue,
    editingDeliveredAtId,
    editDeliveredAt,
    setEditDeliveredAt: onSetEditDeliveredAt,
    isSavingDeliveredAt,
    startEditDeliveredAt: onStartEditDeliveredAt,
    cancelEditDeliveredAt: onCancelEditDeliveredAt,
    saveEditDeliveredAt: onSaveEditDeliveredAt,
  } = hook;

  // CS 응대 메모 — 결과에 걸린 주문들을 주문 축으로 한 번에 조회하고(쿠폰 단위 메모 포함)
  // 쿠폰별로 갈라 쓴다. 백엔드 미배포면 unavailable로 떨어져 배지만 비활성.
  const csMemoOrderIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of results) {
      const raw = c.giftOrder as GiftCouponOrderInfo | string | undefined;
      const id = typeof raw === 'string' ? raw : raw?._id;
      if (id) ids.add(id);
    }
    return Array.from(ids);
  }, [results]);
  const csMemos = useGiftCsMemosByOrders(csMemoOrderIds, status !== 'loading');
  const csMemoByCoupon = useMemo(() => {
    const map = new Map<string, GiftCsMemo[]>();
    csMemos.memoMap.forEach((memos) => {
      for (const memo of memos) {
        if (!memo.giftCoupon) continue;
        const prev = map.get(memo.giftCoupon);
        if (prev) prev.push(memo);
        else map.set(memo.giftCoupon, [memo]);
      }
    });
    return map;
  }, [csMemos.memoMap]);
  /** 그 쿠폰이 속한 주문의 메모를 이미 받아왔는지 (아니면 개수 미상으로 둔다) */
  const csMemosForCoupon = (coupon: GiftCouponForRedemption) => {
    const raw = coupon.giftOrder as GiftCouponOrderInfo | string | undefined;
    const orderId = typeof raw === 'string' ? raw : raw?._id;
    if (!orderId || !csMemos.memoMap.has(orderId)) return undefined;
    return csMemoByCoupon.get(coupon._id) ?? [];
  };

  if (status === 'loading') {
    return (
      <div className="center-box py-12">
        <Loading />
      </div>
    );
  }

  return (
    <>
      {/* 액션 바 */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-sm text-gray-500">검색 결과: {results.length}건</p>
        <div className="flex items-center gap-2">
          {results.length > 0 && (
            <>
              <button
                onClick={onToggleChannelEditMode}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                  channelEditMode
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {channelEditMode ? '매체변경 OFF' : '매체변경 모드'}
              </button>
              <button
                onClick={onUnshare}
                disabled={isUnsharing || selectedShareableCount === 0}
                className="flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isUnsharing ? '처리 중...' : `미전달 처리 (${selectedShareableCount}건)`}
              </button>
            </>
          )}
        </div>
      </div>

      {/* 매체 일괄변경 바 */}
      {channelEditMode && channelSelectedIds.size > 0 && (
        <div className="flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2">
          <span className="text-xs font-medium text-blue-700">
            {channelSelectedIds.size}건 선택
          </span>
          <select
            value={bulkChannelValue}
            onChange={(e) => setBulkChannelValue(e.target.value as DeliveryChannel | '')}
            disabled={isSavingChannel}
            className="rounded border border-blue-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none"
          >
            {DELIVERY_CHANNEL_LIST.map((ch) => (
              <option key={ch} value={ch}>
                {formatDeliveryChannelSelectLabel(ch)}
              </option>
            ))}
            <option value="">입력안함</option>
          </select>
          <button
            onClick={onBulkChannelApply}
            disabled={isSavingChannel}
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSavingChannel ? '처리중...' : '매체 변경'}
          </button>
          <button
            onClick={() => setChannelSelectedIds(new Set())}
            disabled={isSavingChannel}
            className="rounded border border-gray-300 bg-white px-3 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            선택 해제
          </button>
        </div>
      )}

      {/* 테이블 */}
      {results.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={
                      results.filter(
                        (c) => c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
                      ).length > 0 &&
                      results
                        .filter(
                          (c) => c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
                        )
                        .every((c) => selectedIds.has(c._id))
                    }
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상태
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  공유
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <div>쿠폰번호</div>
                  <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                    _id
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  상품
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  주문자
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  구매일
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  메모
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  title="CS 응대 메모 (클릭 시 메모장)"
                >
                  CS메모
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  title="카카오톡 공유 웹훅으로 집계된 전송 횟수 (클릭 시 상세 이력)"
                >
                  카톡공유
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  수신자
                </th>
                <th
                  className="cursor-pointer select-none px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-indigo-600"
                  onClick={onSort}
                >
                  <div className="inline-flex items-center gap-1">
                    신청일시
                    <span className="text-indigo-600">
                      {sortDirection === 'desc' ? '▼' : '▲'}
                    </span>
                  </div>
                  <div className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
                    만료일
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  발송일시
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  도매가
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  매체
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  액션
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {results.map((coupon, idx) => {
                const curDeliveredKey =
                  coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                  coupon.gifticon?.deliveredAt
                    ? formatDateTimeSec(coupon.gifticon.deliveredAt)
                    : null;
                const prevCoupon = idx > 0 ? results[idx - 1] : null;
                const prevDeliveredKey =
                  prevCoupon?.redeemStatus === GiftCouponRedeemStatus.Delivered &&
                  prevCoupon?.gifticon?.deliveredAt
                    ? formatDateTimeSec(prevCoupon.gifticon.deliveredAt)
                    : null;
                const isNewDeliveredGroup =
                  channelEditMode &&
                  curDeliveredKey != null &&
                  idx > 0 &&
                  curDeliveredKey !== prevDeliveredKey;

                return (
                  <CouponTableRow
                    key={coupon._id}
                    coupon={coupon}
                    isNewDeliveredGroup={isNewDeliveredGroup}
                    isSelected={selectedIds.has(coupon._id)}
                    onToggleSelect={toggleSelect}
                    isEditing={editingCouponId === coupon._id}
                    editPhone={editPhone}
                    editMemo={editMemo}
                    isSavingEdit={isSavingEdit}
                    onSetEditPhone={onSetEditPhone}
                    onSetEditMemo={onSetEditMemo}
                    onStartEdit={onStartEdit}
                    onSaveEdit={onSaveEdit}
                    onCancelEdit={onCancelEdit}
                    isEditingDeliveredAt={editingDeliveredAtId === coupon._id}
                    editDeliveredAt={editDeliveredAt}
                    isSavingDeliveredAt={isSavingDeliveredAt}
                    onSetEditDeliveredAt={onSetEditDeliveredAt}
                    onStartEditDeliveredAt={onStartEditDeliveredAt}
                    onSaveEditDeliveredAt={onSaveEditDeliveredAt}
                    onCancelEditDeliveredAt={onCancelEditDeliveredAt}
                    channelEditMode={channelEditMode}
                    isChannelSelected={channelSelectedIds.has(coupon._id)}
                    deliveredGroupMap={deliveredGroupMap}
                    channelSelectedIds={channelSelectedIds}
                    onToggleChannelOne={toggleChannelOne}
                    onToggleChannelGroup={toggleChannelGroup}
                    isDeletingThis={deletingId === coupon._id}
                    onDeleteAdditional={onDeleteAdditional}
                    isIssuing={isIssuing}
                    onIssueRefund={onIssueRefund}
                    onIssueRenewal={onIssueRenewal}
                    onCancelIssue={onCancelIssue}
                    onResetShareStatus={onResetShareStatus}
                    onOpenSignatureModal={onOpenSignatureModal}
                    onOpenVerificationModal={onOpenVerificationModal}
                    onOpenKakaoShareCallbacks={onOpenKakaoShareCallbacks}
                    onOpenOrderModalByPhone={onOpenOrderModalByPhone}
                    onCopyToClipboard={onCopyToClipboard}
                    csMemos={csMemosForCoupon(coupon)}
                    csMemoLoading={csMemos.isLoading}
                    csMemoUnavailable={csMemos.unavailable}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-8 text-center">
          <p className="text-gray-500">검색 결과가 없습니다.</p>
        </div>
      )}
    </>
  );
};
